// On-device photo compression. iOS Safari hands us full-res HEIC/JPEG from the
// camera; we shrink it before it ever touches IndexedDB so storage stays small
// and the nightly sync is cheap. browser-image-compression runs in a Web Worker
// and normalises EXIF orientation (a known iOS gotcha if you draw to canvas
// yourself), so we lean on it rather than hand-rolling canvas compression.

import imageCompression from 'browser-image-compression';
import { PHOTO_COMPRESSION, type PhotoKind } from '../config';
import type { StoredPhoto } from '../db/schema';

/** Cheap unique id without pulling in a uuid dep (crypto.randomUUID is on iOS 16+). */
export function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // Fallback for very old WebViews.
  return 'id-' + Math.abs(Date.now() ^ Math.floor(performance.now() * 1000)).toString(36);
}

async function dimensions(blob: Blob): Promise<{ width: number; height: number }> {
  try {
    const bmp = await createImageBitmap(blob);
    const d = { width: bmp.width, height: bmp.height };
    bmp.close();
    return d;
  } catch {
    return { width: 0, height: 0 };
  }
}

/** Compress a camera/file image into a StoredPhoto ready for IndexedDB. */
export async function compressToStoredPhoto(
  file: File,
  boothId: string,
  kind: PhotoKind,
  createdAt: string,
): Promise<StoredPhoto> {
  let blob: Blob = file;
  try {
    blob = await imageCompression(file, {
      maxSizeMB: PHOTO_COMPRESSION.maxSizeMB,
      maxWidthOrHeight: PHOTO_COMPRESSION.maxWidthOrHeight,
      useWebWorker: PHOTO_COMPRESSION.useWebWorker,
      fileType: 'image/jpeg',
    });
  } catch (err) {
    // Reliability over fidelity: if compression fails, keep the original bytes
    // rather than losing the capture. Sync will still carry it.
    console.warn('photo compression failed, storing original', err);
  }
  const { width, height } = await dimensions(blob);
  // Store the raw bytes as an ArrayBuffer (not the Blob) — iOS/WebKit IndexedDB
  // round-trips buffers reliably where a Blob put can fail. Rebuilt via photoBlob.
  const buffer = await blob.arrayBuffer();
  return {
    local_id: newId(),
    booth_id: boothId,
    kind,
    buffer,
    type: blob.type || 'image/jpeg',
    width,
    height,
    bytes: blob.size,
    synced: false,
    created_at: createdAt,
  };
}
